import axios, {AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, AxiosRequestConfig } from 'axios';
import { handleSessionFailure, isSessionBlocked } from '@/session/sharedSession'

const OMS_API_PREFIX = '/gateway/apisvc-uots-oms';

const config = {
    timeout: Number(process.env.VUE_APP_TIMEOUT) || 10000 // 从环境变量获取超时时间
}

interface ResultData<T> {
    code: number | string;
    data: T;
    message: string;
}

interface RequestConfig extends AxiosRequestConfig {
    query?: object;
    [key: string]: any; // 其他配置（如 headers、timeout 等）
}

class RequestHttp {
    service: AxiosInstance;
    constructor(config: AxiosRequestConfig) {
        this.service = axios.create(config);

        const isOmsApi = (url?: string) => (url || '').includes(OMS_API_PREFIX);

        // 请求拦截器
        this.service.interceptors.request.use(
            (config: InternalAxiosRequestConfig) => {
                if (!isOmsApi(config.url) && isSessionBlocked()) {
                    return Promise.reject(new axios.CanceledError('共享会话已结束'));
                }
                // 可以在这里添加请求头等配置
                if (config.headers && typeof config.headers.set === "function") {
                    const tokenKey = isOmsApi(config.url) ? 'omsToken' : 'Authorization';
                    config.headers.set("Authorization", `Bearer ${localStorage.getItem(tokenKey) || ''}`);
                }
                return config;
            },
            (error: any) => {
                console.error('请求错误:', error);
                return Promise.reject(error);
            }
        );

        // 响应拦截器
        this.service.interceptors.response.use(
            (response: AxiosResponse) => {
                const res = response.data;
                if (!isOmsApi(response.config?.url) && !(res instanceof Blob)
                    && handleSessionFailure(response.status, res)) {
                    return Promise.reject(new axios.CanceledError('登录状态异常'));
                }
                // 如果响应类型是blob，我们返回整个response
                if (response?.config?.responseType === 'blob') {
                    return response;
                }
                return res;
            },
            async (error: any) => {
                if (error?.response?.status === 401 && !isOmsApi(error?.config?.url)) {
                    let body = error.response.data;
                    if (body instanceof Blob) {
                        try { body = JSON.parse(await body.text()); } catch { body = null; }
                    }
                    handleSessionFailure(401, body);
                }
                console.error('响应错误:', error);
                return Promise.reject(error);
            }
        );
    }

    /**
    * @description 常用请求方法封装
    */
    get<T>(url: string, params?: object, _object = {}): Promise<ResultData<T>> {
        return this.service.get(url, { params, ..._object });
    }
    post<T>(url: string, body?: object | string | null, config: RequestConfig = {}): Promise<ResultData<T>> {
        const { query, ...axiosConfig } = config;
        return this.service.post(url, body, {
            ...axiosConfig,
            params: query
        });
    }

}

export default new RequestHttp(config);
